import { useState, useCallback, useRef, useEffect } from 'react';
import './App.css';
import {
  STEPS, PRESENTIEL_STEPS, MASSAGE_STEPS,
  INITIAL_FORM, PRESENTIEL_INITIAL_FORM, MASSAGE_INITIAL_FORM,
  FORMULES, CATEGORIES, PRESENTIEL_PACKS, NUTRITION_INITIAL_FORM,
} from './formSteps';

function generateId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
  });
}

function ensureCustomRate(form, categorie) {
  if (form.customRate) return form;
  const key = form.formule || (categorie === 'massage' ? 'massage' : null);
  const montant = key && FORMULES[key]?.montant;
  if (montant) return { ...form, customRate: String(montant) };
  return form;
}
// V94.58 : permet de fetch les statuts clientes (incl. nouveaux ressentis)
// pour alimenter la cloche de notification d'Anissa, et marquer comme
// 'reviewed' au clic sur une notif ressenti.
import { fetchClientsStatus, clearStatusCache } from './services/fetchClientsStatus';
import { markClientReviewed } from './services/markClientReviewed';
// V97.9 : creation cliente decouplee de l'activation app + envoi pre-q.
// Les imports clientAppFetch / coachIdentity / clientAppUrl sont passes
// dans StepAnamnesis (ClientJourneyPage.jsx) qui gere ces actions.
import SharedCalendar from './SharedCalendar';
import AnissaDashboard from './AnissaDashboard';
import AnissaClientForm from './AnissaClientForm';
import NutritionHistory from './NutritionHistory';
import SupplementsLibrary from './SupplementsLibrary';
import ClientJourneyPage from './ClientJourneyPage';
import LoginScreen from './LoginScreen';
import { getClients, getClient, saveClient, saveClientConfirmed, addGeneration, exportAllData, importAllData, pullFromCloud, retrySyncQueue, getSharedClients, getAnissaOwnClients, saveNutritionConsultation, getNutritionConsultations, updateInterviewNotes, updateClientSection, getNotifications, getUnreadNotificationCount, markNotificationRead, markAllNotificationsRead, syncReminderNotifications, purgeExpiredDrafts, saveApiKeyToCloud, loadApiKeyFromCloud, syncPackNotifications, syncCompletedStepsFromReviews } from './store';
import { ANCRE_VERROU_QUESTIONNAIRE } from './services/questionnaireVerrouillage';
import {
  PACK_DEFINITIONS,
  updateStepStatus,
  canSendPackReview,
  getPackAccess,
  resolveSentPackReviewTemplate,
} from './services/packSystem';
import { listSharedEvents, buildAgendaAlerts } from './services/sharedEvents';
// 2026-07-21 (validation Anissa) : couplage sans-chiffres → suivi de poids.
import { updateClientAppConfig } from './services/clientAppConfig';
import { supabase, isCloudEnabled } from './supabaseClient';
import { purgerBrouillonsAnciens, purgerBrouillonsLocaux } from './suivi/services/suiviBrouillonLocal';
import ReminderPanel, { getReminderCount } from './ReminderPanel';
import { getT } from './translations';
import QuestionnaireClient from './QuestionnaireClient';
import AnamneseClientEn from './AnamneseClientEn';
import CycleReviewForm from './CycleReviewForm';
import Decouverte from './Decouverte';
// 2026-07-19 — Espace Suivi transversal (boîte de réception d'Anissa)
import SuiviCockpit from './SuiviCockpit';

// Per-category short-label key map for the step navigator in the form header.
const STEP_LABEL_KEYS = {
  online: {
    1: 'steplabel.identite', 2: 'steplabel.objectifs', 3: 'steplabel.sport', 4: 'steplabel.sante',
    5: 'steplabel.nutrition', 6: 'steplabel.lifestyle', 7: 'steplabel.contexte', 8: 'steplabel.mesNotes',
  },
  presentiel: {
    1: 'steplabel.identite', 2: 'steplabel.objectifs', 3: 'steplabel.sport', 4: 'steplabel.sante',
    5: 'steplabel.nutrition', 6: 'steplabel.lifestyle', 7: 'steplabel.mesNotes',
  },
};

const ANISSA_LOGO = 'https://cdn.prod.website-files.com/699eb56ec2e8b94e41cfa06c/69d411dfafbbe967e3d992c4_Design_sans_titre_1_-removebg-preview.png';

function getStepsForCategory(cat) {
  if (cat === 'massage') return MASSAGE_STEPS;
  if (cat === 'presentiel') return PRESENTIEL_STEPS;
  return STEPS;
}

function getInitialFormForCategory(cat) {
  if (cat === 'massage') return MASSAGE_INITIAL_FORM;
  if (cat === 'presentiel') return PRESENTIEL_INITIAL_FORM;
  return INITIAL_FORM;
}

function getStepIcons(cat) {
  const steps = getStepsForCategory(cat);
  return steps.map((_, i) => String(i + 1).padStart(2, '0'));
}

// Toast component
// V94.14 : support type (success/info/warning/error) + icone + style sémantique
function Toast({ message, visible, type = 'success' }) {
  if (!visible) return null;
  const COLORS = {
    success: { bg: 'rgba(106,191,138,.95)', icon: '\u2713', border: 'rgba(106,191,138,1)' },
    info:    { bg: 'rgba(96,165,250,.95)',  icon: 'i', border: 'rgba(96,165,250,1)' },
    warning: { bg: 'rgba(232,160,64,.95)',  icon: '!', border: 'rgba(232,160,64,1)' },
    error:   { bg: 'rgba(212,92,76,.95)',   icon: '\u2715', border: 'rgba(212,92,76,1)' },
  };
  const c = COLORS[type] || COLORS.success;
  return (
    <div
      className="toast-notification"
      style={{
        background: c.bg,
        borderLeft: `4px solid ${c.border}`,
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
      }}
    >
      <span style={{
        width: 22, height: 22, borderRadius: '50%',
        background: 'rgba(255,255,255,.25)',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 700, fontSize: '.85rem', flexShrink: 0,
      }}>{c.icon}</span>
      <span>{message}</span>
    </div>
  );
}

// V94.14 : avertissement bandeau si Anissa ouvre le SaaS sur un ecran < 1024px
// (l'editeur est desktop-only, mobile casse l'experience)
// 2026-07-26 — Suivi mobile : la vue Suivi est désormais pensée pour le
// téléphone, le bandeau y serait faux → prop suppress.
function DesktopOnlyBanner({ suppress = false }) {
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem('bfc_dismiss_desktop_warn') === '1');
  useEffect(() => {
    const check = () => setShow(window.innerWidth < 1024);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  if (!show || dismissed || suppress) return null;
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 10000,
      background: 'rgba(232,160,64,.95)', color: '#1a1a14',
      padding: '10px 16px', fontSize: '.85rem', fontWeight: 500,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 12, boxShadow: '0 2px 12px rgba(0,0,0,.3)',
    }}>
      <span>
        <span style={{ marginRight: 8 }}>{'\u26a0\ufe0f'}</span>
        Cet espace est conçu pour ordinateur. Sur tablette/téléphone certaines fonctions sont limitées.
      </span>
      <button
        type="button"
        onClick={() => { setDismissed(true); sessionStorage.setItem('bfc_dismiss_desktop_warn', '1'); }}
        style={{
          background: 'rgba(0,0,0,.15)', border: 'none', borderRadius: 6,
          padding: '4px 10px', cursor: 'pointer', color: '#1a1a14',
          fontSize: '.8rem', fontWeight: 500,
        }}
      >Compris</button>
    </div>
  );
}

// V94.14 : compteur de caracteres reutilisable pour textareas longs
// Usage: <CharCounter value={text} max={500} />
export function CharCounter({ value, max = null, soft = null }) {
  const len = (value || '').length;
  let color = 'rgba(255,255,255,.35)';
  let label = `${len}${max ? ` / ${max}` : ''} caractères`;
  if (max && len > max) { color = '#d4806c'; label = `${len} / ${max} (dépassé)`; }
  else if (soft && len > soft) { color = '#e8a040'; }
  return (
    <span style={{ fontSize: '.7rem', color, marginTop: 3, display: 'block' }}>
      {label}
    </span>
  );
}

// V94.14 : empty state reutilisable avec icone + titre + description + CTA optionnel
// Charte cockpit : zero emoji rendu. `icon` est optionnel \u2014 s'il n'est pas
// fourni, aucune pastille emoji n'est affichee (le titre + description suffisent).
export function EmptyState({ icon = null, title, description, action }) {
  return (
    <div style={{
      padding: '48px 24px', textAlign: 'center',
      background: 'rgba(255,255,255,.02)', borderRadius: 12,
      border: '1px dashed rgba(255,255,255,.08)',
    }}>
      {icon && <div style={{ fontSize: '2.4rem', marginBottom: 12, opacity: .5 }}>{icon}</div>}
      <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>{title}</div>
      {description && (
        <div style={{ fontSize: '.82rem', color: 'var(--text-muted)', marginBottom: 16, maxWidth: 380, margin: '0 auto 16px' }}>
          {description}
        </div>
      )}
      {action}
    </div>
  );
}

// Version badge — discret en bas a droite, utile pour verifier le cache.
// V94.25 : injection automatique au build (vite.config.js define).
//   __APP_VERSION__ = 'V' + package.json.version  (bump via `npm version patch`)
//   __BUILD_HASH__  = git rev-parse --short HEAD   (commit hash court)
//   __BUILD_DATE__  = YYYY-MM-DD                   (date du build)
// Plus besoin de bump APP_VERSION manuellement.
// Globals declares dans .eslintrc.js + remplaces au build par Vite.
const APP_VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'Vdev';
const BUILD_HASH = typeof __BUILD_HASH__ !== 'undefined' ? __BUILD_HASH__ : 'dev';
const BUILD_DATE = typeof __BUILD_DATE__ !== 'undefined' ? __BUILD_DATE__ : '';
function VersionBadge() {
  return (
    <div
      title={`Build ${BUILD_DATE} · commit ${BUILD_HASH}`}
      style={{
        position: 'fixed',
        bottom: 6,
        right: 8,
        zIndex: 9999,
        fontSize: 10,
        fontFamily: 'ui-monospace, monospace',
        // V97.11.3 : couleur noire pour etre lisible sur fond ivoire
        // (theme Anissa) ET sur fond sombre. Background blanc semi-opaque
        // donne le contraste sur les deux themes.
        color: 'rgba(0, 0, 0, 0.75)',
        background: 'rgba(255, 255, 255, 0.85)',
        padding: '2px 6px',
        borderRadius: 4,
        letterSpacing: '.5px',
        pointerEvents: 'auto',
        userSelect: 'none',
      }}
    >
      {APP_VERSION}
    </div>
  );
}

// Public questionnaire route — no auth required
function getQuestionnaireClientId() {
  const match = window.location.pathname.match(/^\/questionnaire\/([a-f0-9-]+)$/i);
  return match ? match[1] : null;
}

// V86.2 : Public anamnese EN route — no auth required
// Utilisee pour les clientes Benfitcoach EN (suivi/intensif + langue EN)
// qui recoivent l'anamnese complete directement, sans pre-questionnaire FR.
function getAnamneseEnClientId() {
  const match = window.location.pathname.match(/^\/anamnese\/([a-f0-9-]+)$/i);
  return match ? match[1] : null;
}

// Public cycle review route — no auth required
function getCycleReviewToken() {
  const match = window.location.pathname.match(/^\/review\/([a-f0-9-]+)$/i);
  return match ? match[1] : null;
}

// Suivi : au démarrage, les copies locales de plus de 48 h sont effacées.
purgerBrouillonsAnciens();

function App() {
  // Public routes — no auth required
  const cycleReviewToken = getCycleReviewToken();
  if (cycleReviewToken) {
    return <CycleReviewForm token={cycleReviewToken} />;
  }
  const questionnaireClientId = getQuestionnaireClientId();
  if (questionnaireClientId) {
    return <QuestionnaireClient clientId={questionnaireClientId} />;
  }
  // V86.4 : route publique /anamnese/:id -> AnamneseClientEn (8 steps EN)
  const anamneseEnClientId = getAnamneseEnClientId();
  if (anamneseEnClientId) {
    return <AnamneseClientEn clientId={anamneseEnClientId} />;
  }
  if (window.location.pathname === '/decouverte') {
    return <Decouverte />;
  }

  return <MainApp />;
}

// V97.34 fix react-hooks/rules-of-hooks : les early returns des routes
// publiques ci-dessus vivaient dans le meme composant que ~40 hooks →
// hooks appeles conditionnellement (fonctionnait par chance car la
// condition est stable au montage, mais fragile avec React 19 / compiler).
// Les hooks vivent desormais dans MainApp, monte uniquement hors routes
// publiques. Strictement additif : aucun comportement modifie.
function MainApp() {
  // Auth state
  const [authenticated, setAuthenticated] = useState(() => {
    if (!isCloudEnabled) return true;
    // 2026-07-26 — Suivi mobile : le drapeau vit en localStorage pour que la
    // session survive aux relances de la PWA sur le téléphone d'Anissa (la
    // session Supabase, elle, est déjà persistée en localStorage par le SDK).
    // sessionStorage reste lu pour les sessions ouvertes avant la migration.
    return localStorage.getItem('bfc_auth') === 'true' || sessionStorage.getItem('bfc_auth') === 'true';
  });
  const [currentUser, setCurrentUser] = useState(() => {
    // 2026-07-21 — Espace 100 % Anissa : défaut Anissa (retrait partie Benoit).
    return localStorage.getItem('bfc_user') || sessionStorage.getItem('bfc_user') || 'Anissa';
  });
  const [cloudSynced, setCloudSynced] = useState(false);
  const [syncStatus, setSyncStatus] = useState('');

  const [apiKey, setApiKey] = useState(() => localStorage.getItem('bfc_api_key') || '');
  // Phase D : detection URL initiale /parcours/:clientId au mount, pour
  // permettre les liens directs / bookmarks / reload au milieu d'un parcours.
  const initialJourneyMatch = window.location.pathname.match(/^\/parcours\/([a-f0-9-]+)$/i);
  // 2026-07-26 — Suivi mobile : sur téléphone, Anissa arrive directement sur
  // le Suivi (usage nomade : vacances, attentes, entre deux). Le Dashboard
  // reste la porte d'entrée sur ordinateur.
  const [page, setPage] = useState(() => {
    if (initialJourneyMatch) return 'clientJourney';
    try {
      if (window.matchMedia('(max-width: 900px)').matches) return 'anissaSuivi';
    } catch { /* environnements sans matchMedia (tests) */ }
    return 'dashboard';
  });
  const [clientId, setClientId] = useState(initialJourneyMatch ? initialJourneyMatch[1] : null);
  const [resumeProgramUpdateScope, setResumeProgramUpdateScope] = useState(null);
  // V97.85 — guidage post-création : bandeau « prochaine étape » sur la fiche
  // de la cliente qu'on vient de créer (fin du « rien ne se passe »).
  const [justCreatedId, setJustCreatedId] = useState(null);
  const [categorie, setCategorie] = useState('online');
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(INITIAL_FORM);
  const [originalForm, setOriginalForm] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState(null);
  const [clients, setClients] = useState(getClients);
  const [editTab, setEditTab] = useState('form');
  const [, setTick] = useState(0);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [convertMode, setConvertMode] = useState(null);
  const [editingConsultation, setEditingConsultation] = useState(null);
  const [toast, setToast] = useState({ message: '', visible: false, type: 'success' });
  const [showReminders, setShowReminders] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notifCount, setNotifCount] = useState(0);
  // V39 : événements agenda partagés (pour alimenter les rappels cloche côté Anissa)
  const [agendaEvents, setAgendaEvents] = useState([]);

  // V94.58 : helper qui calcule les notifs "feedback recu" pour Anissa.
  // Une notif par cliente avec new_feedbacks_count > 0. Notifs virtuelles
  // (pas stockees DB) car auto-derives du dernier last_reviewed_at.
  const buildFeedbackNotifs = async (anissaClients) => {
    if (!Array.isArray(anissaClients) || anissaClients.length === 0) return [];
    // V94.66 : envoie {email, stagingClientId} pour matching robuste
    // (cf. fetchClientsStatus). Permet de retrouver la cliente meme quand
    // l'email Apple/Google sur le compte App Store differe de l'email SaaS.
    const items = anissaClients
      .map((c) => ({
        email: (c.email || '').trim().toLowerCase() || null,
        stagingClientId: c.stagingClientId || null,
      }))
      .filter((x) => x.email || x.stagingClientId);
    if (items.length === 0) return [];
    let statuses = {};
    try {
      statuses = await fetchClientsStatus(items);
    } catch {
      return []; // best-effort, pas bloquant
    }
    const out = [];
    for (const c of anissaClients) {
      const email = (c.email || '').trim().toLowerCase();
      const stagingId = c.stagingClientId || null;
      if (!email && !stagingId) continue;
      const key = stagingId ? `id:${stagingId}` : email;
      const s = statuses[key];
      const n = s?.new_feedbacks_count || 0;
      if (n <= 0) continue;
      const plural = n > 1;
      out.push({
        id: `feedback-${c.id}`,
        type: 'feedback_received',
        clientId: c.id,
        clientName: c.prenom || c.first_name || 'Cliente',
        message: `${c.prenom || c.first_name || 'Cliente'} : ${n} nouveau${plural ? 'x' : ''} ressenti${plural ? 's' : ''}`,
        date: s?.last_activity_at || new Date().toISOString(),
        read: false,
      });
    }
    return out;
  };

  // Load notifications from Supabase + sync local reminders
  const loadNotifications = async () => {
    // Sync local reminder notifications
    let anissaClients = [];
    if (currentUser === 'Anissa') {
      anissaClients = [...getSharedClients(), ...getAnissaOwnClients()];
      syncReminderNotifications(anissaClients);
      syncCompletedStepsFromReviews().then(() => {
        syncPackNotifications(anissaClients);
      });
    }

    // V94.58 : notifs feedback (virtuelles) calculees a partir des statuts
    // clientes recuperes via l'API admin clients-status. Affichees a cote
    // des notifs Supabase classiques dans la cloche.
    const feedbackNotifs = currentUser === 'Anissa' ? await buildFeedbackNotifs(anissaClients) : [];

    // Fetch from Supabase
    if (isCloudEnabled) {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(30);
      if (!error && data) {
        // Merge Supabase notifications with local reminders + feedback notifs
        const localReminders = getNotifications().filter(n => n.type === 'consultation_reminder');
        const merged = [...data.map(n => ({
          id: n.id,
          type: n.type,
          clientId: n.client_id,
          clientName: n.client_name,
          message: n.message,
          date: n.created_at,
          read: n.read,
        })), ...localReminders, ...feedbackNotifs].sort((a, b) => new Date(b.date) - new Date(a.date));
        const newCount = merged.filter(n => !n.read).length;
        setNotifCount(prev => prev === newCount ? prev : newCount);
        setNotifications(merged);
        return;
      }
    }

    // Fallback: local only
    const local = getNotifications();
    const merged = [...local, ...feedbackNotifs].sort((a, b) => new Date(b.date) - new Date(a.date));
    setNotifications(merged);
    setNotifCount(merged.filter(n => !n.read).length);
  };

  // 2026-08-22 — ce sondage tournait toutes les 10 secondes, sans jamais
  // s'arrêter, et interrogeait `clients` puis `notifications` à chaque tour.
  // Relevé dans pg_stat_statements : 277 000 requêtes sur `clients` et
  // 234 000 sur `notifications`, pour une dizaine de fiches et huit
  // notifications. C'est ce qui faisait chauffer l'instance, au point que
  // Supabase affichait « high CPU usage » sur le projet.
  //
  // Deux corrections. La cadence passe à 60 secondes : une notification vue
  // une minute plus tard ne change rien à une journée de cabinet. Et le
  // sondage s'arrête quand l'onglet passe en arrière-plan, ce qui est le cas
  // la plupart du temps — Anissa travaille dans Word et sa boîte mail.
  //
  // Au retour de l'onglet on relit tout de suite, sinon elle attendrait
  // jusqu'à une minute devant un écran périmé.
  useEffect(() => {
    let interval = null;

    const arreter = () => {
      if (interval) { clearInterval(interval); interval = null; }
    };
    const demarrer = () => {
      arreter();
      interval = setInterval(loadNotifications, 60000);
    };

    const surVisibilite = () => {
      if (document.visibilityState === 'visible') {
        loadNotifications();
        demarrer();
      } else {
        arreter();
      }
    };

    loadNotifications();
    if (document.visibilityState === 'visible') demarrer();
    document.addEventListener('visibilitychange', surVisibilite);
    return () => {
      arreter();
      document.removeEventListener('visibilitychange', surVisibilite);
    };
  }, [currentUser]);

  // V39 : charge les événements agenda des 2 prochains jours pour alimenter les rappels Anissa
  useEffect(() => {
    if (currentUser !== 'Anissa') return; // côté Benoit, Dashboard gère déjà
    const load = async () => {
      const from = new Date();
      const to = new Date(); to.setDate(to.getDate() + 2);
      try {
        const { data } = await listSharedEvents(from.toISOString(), to.toISOString());
        setAgendaEvents(data || []);
      } catch {
        setAgendaEvents([]);
      }
    };
    load();
    const iv = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(iv);
  }, [currentUser]);
  const [interviewOpen, setInterviewOpen] = useState(false);
  const [interviewNotes, setInterviewNotes] = useState(null);
  const interviewSaveTimer = useRef(null);
  const fileInputRef = useRef(null);

  const isAnissa = currentUser === 'Anissa';
  const isBenoit = currentUser === 'Benoit';

  const refreshClients = useCallback(() => setClients(getClients()), []);

  // V97.8.1 (2026-05-13) — Garde-fou parcours fantôme.
  // Si l'URL au mount est /parcours/<UUID> mais que la cliente n'existe
  // plus (supprimée), on retombe sur le dashboard + on clear l'URL.
  // Sinon le user voit un écran de parcours cassé ou des données
  // fantomes.
  useEffect(() => {
    if (page !== 'clientJourney' || !clientId) return;
    if (clients.length === 0) return; // pas encore chargé
    const exists = clients.some((c) => c.id === clientId);
    if (!exists) {
      setPage('dashboard');
      setClientId(null);
      try {
        if (window.location.pathname.startsWith('/parcours/')) {
          window.history.pushState({}, '', '/');
        }
      } catch { /* history non bloquante */ }
    }
  }, [page, clientId, clients]);
  const forceUpdate = useCallback(() => setTick(t => t + 1), []);

  // V94.14 : showToast accepte (message, type) - rétrocompat positionnel
  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, visible: true, type });
    setTimeout(() => setToast({ message: '', visible: false, type }), 3500);
  }, []);

  // Purge expired drafts on boot (covers !isCloudEnabled case)
  useEffect(() => {
    if (authenticated) purgeExpiredDrafts();
  }, [authenticated]);

  // Load API key from cloud on login if localStorage is empty
  useEffect(() => {
    if (!authenticated || !isCloudEnabled) return;
    if (apiKey) return;
    loadApiKeyFromCloud().then(key => {
      if (key) {
        setApiKey(key);
        localStorage.setItem('bfc_api_key', key);
      }
    });
  }, [authenticated]);

  // Cloud sync on login
  useEffect(() => {
    if (!authenticated || !isCloudEnabled) return;
    purgeExpiredDrafts();
    // Toujours rejouer la queue au démarrage, indépendamment de cloudSynced
    retrySyncQueue();
    if (cloudSynced) return;
    setSyncStatus('syncing');
    pullFromCloud().then(result => {
      setCloudSynced(true);
      if (result.synced) {
        setSyncStatus('synced');
        refreshClients();
      } else {
        setSyncStatus('offline');
      }
      setTimeout(() => setSyncStatus(''), 4000);
    });
  }, [authenticated, cloudSynced, refreshClients]);

  // Retry sync queue when coming back online
  useEffect(() => {
    if (!isCloudEnabled) return;
    const handleOnline = () => {
      retrySyncQueue();
      setSyncStatus('synced');
      setTimeout(() => setSyncStatus(''), 3000);
    };
    const handleOffline = () => setSyncStatus('offline');
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // 2026-08-08 — Cause racine du blocage « La fiche cliente n'est plus
  // accessible » sur staging.
  //
  // `bfc_auth` vivait en localStorage SANS aucun lien avec la session
  // Supabase. Quand cette session expire (ou n'a jamais été ouverte sur
  // cette origine), le cockpit se croit toujours connecté : les requêtes
  // partent alors en rôle `anon`, la RLS de `clients` (policy réservée à
  // `authenticated`) ne renvoie AUCUNE ligne, et la page affiche sans le
  // dire la copie locale de la fiche. La lecture ment, l'écriture non :
  // d'où l'erreur au clic sur « Marquer comme complétée ».
  //
  // On resynchronise les deux : pas de session Supabase vivante, pas de
  // cockpit. Anissa retombe sur l'écran de connexion, qui est actionnable.
  useEffect(() => {
    if (!isCloudEnabled) return undefined;
    let monte = true;
    const perdreLaSession = () => {
      if (!monte) return;
      localStorage.removeItem('bfc_auth');
      sessionStorage.removeItem('bfc_auth');
      setAuthenticated(false);
      setCloudSynced(false);
    };
    // Un incident réseau ne doit PAS déconnecter : on ne coupe que sur un
    // refus d'authentification franc (jeton absent, expiré, révoqué).
    const sessionRefusee = (error) => {
      if (!error) return true; // pas d'erreur et pas d'utilisateur = pas de session
      if (error.name === 'AuthRetryableFetchError') return false;
      return [400, 401, 403].includes(error.status);
    };
    // getUser() valide le jeton côté serveur ; getSession() se contente de
    // relire localStorage et rend volontiers une session déjà expirée.
    supabase.auth.getUser()
      .then(({ data, error }) => {
        if (!data?.user && sessionRefusee(error)) perdreLaSession();
      })
      .catch(() => { /* réseau : incident transitoire, on ne déconnecte pas */ });
    const { data: abonnement } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') perdreLaSession();
    });
    return () => { monte = false; abonnement?.subscription?.unsubscribe(); };
  }, []);

  const handleLogin = useCallback((username) => {
    setCurrentUser(username);
    setAuthenticated(true);
    refreshClients();
  }, [refreshClients]);

  // Show login screen if cloud is enabled and not authenticated
  if (!authenticated) {
    return (
      <>
        <DesktopOnlyBanner />
        <LoginScreen onLogin={handleLogin} />
        <VersionBadge />
      </>
    );
  }

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleApiKeyChange = (e) => {
    const key = e.target.value;
    setApiKey(key);
    localStorage.setItem('bfc_api_key', key);
    if (key.trim()) saveApiKeyToCloud(key.trim());
  };

  // Espace 100 % Anissa (2026-07-21) : handleSave/handleGenerate (dossier IA
  // Benoit), openHistory et la conversion massage ont été retirés avec
  // l'interface Benoit. La création/édition côté Anissa passe par
  // AnissaClientForm + ClientJourneyPage.

  const goToDashboard = () => {
    refreshClients();
    setPage('dashboard');
    setConvertMode(null);
    setMobileMenu(false);
    setClientId(null);
    setResumeProgramUpdateScope(null);
    // V97.8.1 (2026-05-13) : clear l'URL si on était sur /parcours/:id.
    // Sans ça, un F5 relit l'URL et tente de recharger un parcours
    // potentiellement obsolète (cliente supprimée par ex).
    try {
      if (window.location.pathname.startsWith('/parcours/')) {
        window.history.pushState({}, '', '/');
      }
    } catch { /* history non bloquante */ }
  };

  // Les écritures du parcours sont autoritaires en base. Au retour vers le
  // dashboard, on relit donc le cloud avant de recalculer cartes et cloche :
  // cela évite qu'un S4 clôturé reste affiché « en retard » depuis la copie
  // locale chargée à l'ouverture de l'application.
  const handleExitClientJourney = async () => {
    setJustCreatedId(null);
    setPage('dashboard');
    setClientId(null);
    try {
      if (window.location.pathname.startsWith('/parcours/')) {
        window.history.pushState({}, '', '/');
      }
    } catch { /* history non bloquante */ }

    try {
      if (isCloudEnabled) await pullFromCloud();
    } finally {
      refreshClients();
      await loadNotifications();
    }
  };

  // Espace 100 % Anissa (2026-07-21) : les boutons Export/Import JSON du
  // header Benoit ont été retirés avec son interface. exportAllData /
  // importAllData restent disponibles dans store.js si besoin de secours.

  const handleLogout = () => {
    localStorage.removeItem('bfc_auth');
    localStorage.removeItem('bfc_user');
    sessionStorage.removeItem('bfc_auth');
    sessionStorage.removeItem('bfc_user');
    // Suivi : les copies locales des modifications (données de la cliente)
    // ne restent jamais sur le poste après la déconnexion.
    purgerBrouillonsLocaux();
    setAuthenticated(false);
    setCloudSynced(false);
    // 2026-08-08 — Symétrique du garde-fou de session ci-dessus : se
    // déconnecter du cockpit doit aussi fermer la session Supabase, sinon
    // elle survit au poste et les deux états repartent en divergence.
    if (isCloudEnabled) supabase.auth.signOut().catch(() => { /* best-effort */ });
  };

  // Anissa: nutrition consultation handlers
  // Phase H : tout clic 'Consultation' depuis le dashboard ouvre maintenant
  // le PARCOURS de la cliente. NutritionConsultation n'est plus une page
  // autonome — elle vit uniquement embedded dans l'etape 6 du wizard.
  const handleStartConsultation = (id) => {
    setEditingConsultation(null);
    handleOpenClientJourney(id);
  };

  const handleSaveConsultation = (consultation) => {
    saveNutritionConsultation(consultation);
    // La premiere sauvegarde ne demarre jamais la periode du pack. Le depart
    // est atomique avec la publication reussie depuis l'etape Livraison.
    // Les clientes existantes deja confirmees conservent leur date.
    showToast('Consultation sauvegardee avec succes');
    // V85.3 : plus de redirection auto vers dashboard — l'utilisateur reste sur la page
    // pour continuer ses modifications. Il ferme manuellement via le bouton Fermer.
  };

  const handleViewNutritionHistory = (id) => {
    setClientId(id);
    setPage('nutritionHistory');
    setMobileMenu(false);
  };

  const handleEditConsultation = (consultation) => {
    setEditingConsultation(consultation);
    handleOpenClientJourney(consultation.clientId);
  };

  const currentClient = clientId ? getClient(clientId) : null;
  const currentSteps = getStepsForCategory(categorie);
  const stepIcons = getStepIcons(categorie);
  const totalSteps = currentSteps.length;
  const progressPct = ((step - 1) / (totalSteps - 1)) * 100;
  const isMassage = categorie === 'massage';

  // Get shared clients for Anissa or nutrition notes for Benoit
  const sharedClients = getSharedClients();
  const anissaOwnClients = getAnissaOwnClients();

  // Anissa: create new client
  const handleAnissaNewClient = () => {
    setPage('anissaNewClient');
    setMobileMenu(false);
  };

  const handleAnissaSaveClient = (formData, packType) => {
    if (!getPackAccess(packType).known) {
      showToast('Choisis un pack valide avant de creer la fiche', 'warning');
      return;
    }
    const client = saveClient({
      categorie: 'nutrition',
      form: formData,
      prenom: formData.prenom,
      formule: 'nutrition',
      langue: 'FR',
      createdBy: 'anissa',
      packType,
      // V97.8.1 : packStartedAt null à la création (le pack démarre seulement
      // à markDelivered). Voir explication identique l. 1192.
      packStartedAt: null,
      packStartedAtConfirmed: false,
      packSchedule: [],
    });
    refreshClients();
    showToast('Client cree avec succes');
    setTimeout(() => goToDashboard(), 2000);
  };

  // Anissa: open own client for editing
  // Phase H : tout clic 'Ouvrir cliente' depuis le dashboard ouvre le parcours.
  // L'edition du profil cliente (anissaEditClient) reste accessible via le bouton
  // 'Profil' dans le header du parcours (cf. handleEditClientProfile).
  const handleAnissaOpenClient = (id) => {
    const client = getClient(id);
    if (!client) return;
    setForm(client.form || {});
    handleOpenClientJourney(id);
  };

  // Acces a l'edition du profil cliente depuis le header du parcours.
  const handleEditClientProfile = (id, options = {}) => {
    const client = getClient(id);
    if (!client) return;
    setResumeProgramUpdateScope(options.resumeProgramUpdateScope || null);
    setClientId(client.id);
    setForm(client.form || {});
    setPage('anissaEditClient');
    setMobileMenu(false);
    window.history.pushState({}, '', `/`);
  };

  // Phase D : ouvre le parcours wizard pour une cliente. Pousse l'URL pour
  // rester coherent avec la decision "page dediee /parcours/:clientId".
  const handleOpenClientJourney = (id) => {
    if (!id) return;
    setClientId(id);
    setPage('clientJourney');
    setMobileMenu(false);
    window.history.pushState({}, '', `/parcours/${id}`);
  };

  const returnToEditedClientJourney = () => {
    if (!clientId) return goToDashboard();
    handleOpenClientJourney(clientId);
  };

  const handleAdaptPlan = (client, adaptedPlan) => {
    const consultations = getNutritionConsultations(client.id);
    const lastConsultation = consultations[0] || null;
    const weekLabel = `Adaptation bilan 4 semaines`;
    const prefilledConsultation = lastConsultation
      ? {
          ...lastConsultation,
          id: undefined,
          nutritionPlan: adaptedPlan,
          createdAt: new Date().toISOString(),
          status: 'a_valider',
          label: weekLabel,
          consultantName: 'Anissa',
        }
      : {
          clientId: client.id,
          nutritionPlan: adaptedPlan,
          createdAt: new Date().toISOString(),
          status: 'a_valider',
          label: weekLabel,
          consultantName: 'Anissa',
        };
    setEditingConsultation(prefilledConsultation);
    handleOpenClientJourney(client.id);
    showToast('Plan adapté — vérifiez et sauvegardez');
  };

  const handleSendPackReview = async (client, step) => {
    // Protection : vérification finale avant envoi
    if (!canSendPackReview(step)) return;
    // Protection : vérifier que l'étape n'est pas déjà 'sent' ou 'done'
    const currentSchedule = client.packSchedule || [];
    const existingStep = currentSchedule.find(s => s.stepNumber === step.stepNumber);
    if (existingStep?.status === 'sent' || existingStep?.status === 'done') {
      showToast('Ce questionnaire a déjà été envoyé');
      return;
    }

    try {
      const token = generateId();
      const reviewId = generateId();
      const templateType = resolveSentPackReviewTemplate(step);
      if (!templateType) throw new Error('Modèle de bilan invalide');
      let ownerId = null;
      try {
        const { data: { user } } = await supabase.auth.getUser();
        ownerId = user?.id || null;
      } catch {}
      const review = {
        id: reviewId,
        token,
        client_id: client.id,
        consultation_id: null,
        owner_id: ownerId,
        status: 'sent',
        step_number: step.stepNumber,
        template_type: templateType,
        created_at: new Date().toISOString(),
      };
      if (isCloudEnabled) {
        const { error } = await supabase.from('cycle_reviews').insert(review);
        if (error) throw error;
      }
      // Relire le client courant pour éviter un overwrite stale
      const freshClient = getClient(client.id);
      const updatedSchedule = updateStepStatus(
        freshClient || client,
        step.stepNumber,
        {
          status: 'sent',
          reviewId,
          notifiedAt: new Date().toISOString(),
        }
      );
      saveClient({
        ...(freshClient || client),
        packSchedule: updatedSchedule,
      });
      refreshClients();
      const link = `${window.location.origin}/review/${token}`;
      try { await navigator.clipboard.writeText(link); } catch {}
      showToast(`Lien copié — ${step.label}`);
    } catch (err) {
      showToast('Erreur lors de la création du questionnaire — réessayez');
    }
  };

  // ─── ANISSA'S INTERFACE ───
  if (isAnissa) {
    const allAnissaClients = [...sharedClients, ...anissaOwnClients];
    const reminderCount = getReminderCount(allAnissaClients);

    const anissaNavButtons = (
      <>
        <button className={`btn-nav ${page === 'dashboard' ? 'btn-nav-active' : ''}`} onClick={goToDashboard}>Dashboard</button>
        <button className={`btn-nav ${page === 'anissaSuivi' ? 'btn-nav-active' : ''}`} onClick={() => { setPage('anissaSuivi'); setMobileMenu(false); }}>Suivi</button>
        <button className={`btn-nav ${page === 'anissaNewClient' ? 'btn-nav-active' : ''}`} onClick={handleAnissaNewClient}>+ Nouveau client</button>
        <button className={`btn-nav ${page === 'sharedCalendar' ? 'btn-nav-active' : ''}`} onClick={() => { setPage('sharedCalendar'); setMobileMenu(false); }}>Agenda</button>
        <button className={`btn-nav ${page === 'anissaSupplements' ? 'btn-nav-active' : ''}`} onClick={() => { setPage('anissaSupplements'); setMobileMenu(false); }}>Complements</button>
      </>
    );

    return (
      <div className={`app anissa-theme${page === 'nutritionConsultation' || page === 'clientJourney' ? ' app-wide' : ''}`}>
        <DesktopOnlyBanner suppress={page === 'anissaSuivi'} />
        <Toast message={toast.message} visible={toast.visible} type={toast.type} />
        <VersionBadge />

        {/* Header */}
        <div className="header anissa-header">
          <img src={ANISSA_LOGO} alt="Anissa Nutrition" onClick={goToDashboard} className="anissa-header-logo" />
          <div className="header-text" onClick={goToDashboard}>
            <h1>Espace Nutrition</h1>
            <div className="subtitle">Anissa Deroubaix</div>
          </div>
          <div className="header-nav">{anissaNavButtons}</div>
          <div className="header-io">
            {/* Unified notifications bell */}
            <div className="reminder-bell-wrapper" style={{ position: 'relative' }}>
              <button className="reminder-bell" onClick={() => setShowNotifications(prev => !prev)} title="Notifications">
                {/* 2026-07-21 : cloche SVG (currentColor) à la place de l'emoji jaune */}
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ display: 'block' }}>
                  <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.7 21a2 2 0 0 1-3.4 0" />
                </svg>
                {(() => {
                  const agendaCount = buildAgendaAlerts(agendaEvents).length;
                  const total = (notifCount || 0) + agendaCount;
                  return total > 0 ? <span className="reminder-badge" style={{ background: '#f87171' }}>{total}</span> : null;
                })()}
              </button>
              {showNotifications && (() => {
                const notifs = notifications.slice(0, 20);
                const TAG_STYLES = {
                  questionnaire_completed: { label: 'Questionnaire', color: '#4ade80' },
                  consultation_reminder: { label: 'Rappel', color: '#fbbf24' },
                  // V94.58 : nouvelle categorie pour les ressentis recus
                  feedback_received: { label: 'Ressenti', color: '#82c39e' },
                  // Chantier 9b — la cliente signale une correction. Sans
                  // entree ici, l'alerte existait mais s'affichait « Info »
                  // en gris : une demande qui attend une decision se lisait
                  // comme une information sans suite.
                  questionnaire_correction_demandee: { label: 'Correction', color: '#d4a24c' },
                  questionnaire_corrected: { label: 'Questionnaire', color: '#4ade80' },
                };
                return (
                  <div style={{ position: 'absolute', top: '100%', right: 0, width: 360, background: '#1e1e1e', border: '1px solid rgba(255,255,255,.1)', borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,.4)', zIndex: 1000, overflow: 'hidden' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid rgba(255,255,255,.08)' }}>
                      <span style={{ fontSize: '.82rem', fontWeight: 700, color: '#f0f0e8' }}>Notifications</span>
                      <div style={{ display: 'flex', gap: 8 }}>
                        {notifCount > 0 && (
                          <button onClick={async () => {
                            markAllNotificationsRead();
                            if (isCloudEnabled) await supabase.from('notifications').update({ read: true }).eq('read', false);
                            // V97.130 — les notifs de ressenti (feedback_received) sont
                            // virtuelles, dérivées de last_reviewed_at : « Tout marquer lu »
                            // doit AUSSI marquer ces clientes revues, sinon le badge garde
                            // leur compte. On applique markClientReviewed à chacune.
                            const feedbackNotifs = notifications.filter(n => n.type === 'feedback_received' && n.clientId);
                            for (const n of feedbackNotifs) {
                              const cl = getClient(n.clientId);
                              // Si une seule échoue, le badge garde son compte
                              // et « Tout marquer lu » a l'air de ne rien faire.
                              if (cl) {
                                try { await markClientReviewed(cl); }
                                catch (e) { console.warn('[notifications] cliente non marquée revue :', cl.id, e?.message); }
                              }
                            }
                            if (feedbackNotifs.length) clearStatusCache();
                            loadNotifications();
                          }} style={{ background: 'none', border: 'none', color: '#4ade80', fontSize: '.72rem', cursor: 'pointer' }}>Tout marquer lu</button>
                        )}
                        <button onClick={() => { setShowNotifications(false); setShowReminders(true); }} style={{ background: 'none', border: 'none', color: '#8a8a7a', fontSize: '.72rem', cursor: 'pointer' }}>Gerer rappels</button>
                      </div>
                    </div>
                    <div style={{ maxHeight: 380, overflowY: 'auto' }}>
                      {/* V39 : rappels agenda (injectés en haut, tag bleu, clic → ouvre l'agenda partagé) */}
                      {(() => {
                        const agenda = buildAgendaAlerts(agendaEvents);
                        if (agenda.length === 0) return null;
                        return agenda.map((a, idx) => (
                          <div
                            key={`agenda-${a.type}-${idx}`}
                            onClick={() => { setShowNotifications(false); setPage('sharedCalendar'); setMobileMenu(false); }}
                            style={{
                              padding: '10px 14px',
                              borderBottom: '1px solid rgba(255,255,255,.05)',
                              cursor: 'pointer',
                              background: 'rgba(106,182,240,.06)',
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{
                                fontSize: '.65rem', fontWeight: 600, color: '#6ab6f0',
                                background: 'rgba(106,182,240,.18)', padding: '1px 6px',
                                borderRadius: 4, flexShrink: 0,
                              }}>Agenda</span>
                              <span style={{ fontSize: '.8rem', color: '#f0f0e8', fontWeight: 600 }}>{a.message}</span>
                            </div>
                          </div>
                        ));
                      })()}
                      {notifs.length === 0 && buildAgendaAlerts(agendaEvents).length === 0 ? (
                        <div style={{ padding: 20, textAlign: 'center', color: '#8a8a7a', fontSize: '.8rem' }}>Aucune notification</div>
                      ) : notifs.map(n => {
                        const tag = TAG_STYLES[n.type] || { label: 'Info', color: '#8a8a7a' };
                        return (
                          <div
                            key={n.id}
                            onClick={async () => {
                              // V94.58 : feedback_received est virtuel (id 'feedback-...').
                              // On declenche markClientReviewed cote API pour
                              // mettre a jour last_reviewed_at → la notif disparaitra
                              // au prochain polling.
                              if (n.type === 'feedback_received' && n.clientId) {
                                const cl = getClient(n.clientId);
                                if (cl) {
                                  markClientReviewed(cl).catch(() => { /* silent */ });
                                  clearStatusCache(); // force refresh
                                }
                              } else {
                                markNotificationRead(n.id);
                                if (isCloudEnabled && n.type !== 'consultation_reminder') {
                                  await supabase.from('notifications').update({ read: true }).eq('id', n.id);
                                }
                              }
                              loadNotifications();
                              setShowNotifications(false);
                              // Chantier 9b — une demande de correction ouvre
                              // la fiche AU bloc du verrou. L'ancre est posee
                              // avant l'ouverture : c'est le bloc lui-meme qui
                              // la lit au montage, puis l'efface. Pas de
                              // routage a inventer pour une intention qui ne
                              // survit pas a l'ecran suivant.
                              if (n.type === 'questionnaire_correction_demandee') {
                                try {
                                  window.location.hash = ANCRE_VERROU_QUESTIONNAIRE;
                                } catch { /* pas d'ancre, la fiche s'ouvre quand meme */ }
                              }
                              if (n.type === 'consultation_reminder' && n.clientId) {
                                handleStartConsultation(n.clientId);
                              } else if (n.clientId) {
                                const cl = getClient(n.clientId);
                                if (cl?.createdBy === 'anissa') handleAnissaOpenClient(n.clientId);
                                else handleStartConsultation(n.clientId);
                              }
                            }}
                            style={{ padding: '10px 14px', borderBottom: '1px solid rgba(255,255,255,.05)', cursor: 'pointer', background: n.read ? 'transparent' : 'rgba(74,222,128,.06)' }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ fontSize: '.65rem', fontWeight: 600, color: tag.color, background: tag.color + '18', padding: '1px 6px', borderRadius: 4, flexShrink: 0 }}>{tag.label}</span>
                              <span style={{ fontSize: '.8rem', color: n.read ? '#8a8a7a' : '#f0f0e8', fontWeight: n.read ? 400 : 600 }}>{n.message}</span>
                            </div>
                            <div style={{ fontSize: '.68rem', color: '#6b5f48', marginTop: 3, paddingLeft: 2 }}>{new Date(n.date).toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>
            <span className="user-badge anissa-user-badge">Connecte : Anissa</span>
            {isCloudEnabled && syncStatus && (
              <span className={`sync-badge sync-${syncStatus}`}>
                {syncStatus === 'syncing' && 'Sync...'}
                {syncStatus === 'synced' && 'Synced'}
                {syncStatus === 'offline' && 'Offline'}
              </span>
            )}
            <button className="btn-nav" onClick={handleLogout}>Deconnexion</button>
          </div>
          <button className="hamburger" onClick={() => setMobileMenu(!mobileMenu)}>
            <span /><span /><span />
          </button>
        </div>

        {/* Reminder panel */}
        {showReminders && (
          <ReminderPanel
            clients={allAnissaClients}
            onClose={() => setShowReminders(false)}
            onConsultation={handleStartConsultation}
            onOpenClient={(id) => {
              const client = getClient(id);
              if (client?.createdBy === 'anissa') handleAnissaOpenClient(id);
              else handleViewNutritionHistory(id);
            }}
          />
        )}

        {/* Mobile menu */}
        <div className={`mobile-menu ${mobileMenu ? 'open' : ''}`}>
          {anissaNavButtons}
          <span className="user-badge anissa-user-badge" style={{ padding: '10px 16px' }}>Connecte : Anissa</span>
          <button className="btn-nav" onClick={() => { handleLogout(); setMobileMenu(false); }}>Deconnexion</button>
        </div>

        {/* API Key — dev uniquement, en prod la cle est cote serveur (ANTHROPIC_API_KEY) */}
        {import.meta.env.DEV && (
          <div className="api-key-bar anissa-api-key-bar">
            <label>API Key</label>
            <input type="password" value={apiKey} onChange={handleApiKeyChange} placeholder="sk-ant-..." />
          </div>
        )}

        {/* Dashboard */}
        {page === 'dashboard' && (
          <AnissaDashboard
            sharedClients={sharedClients}
            ownClients={anissaOwnClients}
            onOpenSuivi={() => { setPage('anissaSuivi'); setMobileMenu(false); }}
            onConsultation={handleStartConsultation}
            onEditConsultation={handleEditConsultation}
            onViewHistory={handleViewNutritionHistory}
            onNewClient={handleAnissaNewClient}
            onOpenClient={handleAnissaOpenClient}
            onRefresh={refreshClients}
            onAdaptPlan={handleAdaptPlan}
            onSendPackReview={handleSendPackReview}
          />
        )}

        {/* 2026-07-19 — Espace Suivi transversal : silences + flux des
            ressentis toutes clientes, Vu / Répondre inline. */}
        {page === 'anissaSuivi' && (
          <SuiviCockpit
            sharedClients={sharedClients}
            ownClients={anissaOwnClients}
            onOpenClient={handleAnissaOpenClient}
          />
        )}

        {/* New client form */}
        {page === 'anissaNewClient' && (
          <AnissaClientForm
            onSave={handleAnissaSaveClient}
            onSaveQuick={async (formData, packType) => {
              // V97.9 (2026-05-13) — Création découplée.
              // Avant : creation + invite-client + ouverture Gmail (3 actions
              // automatiques dans la foulee). Maintenant : on cree juste la
              // fiche et on emmene Anissa sur le parcours. Elle decide
              // ensuite, depuis l'etape Onboarding, quand activer l'app
              // (BLOC 1) et quand envoyer le pre-questionnaire (BLOC 2).
              // Pourquoi : philosophie produit "Anissa garde la main",
              // possibilite de verifier la fiche avant d'envoyer, evite
              // les mails partis trop tot.
              if (!getPackAccess(packType).known) {
                showToast('Choisis un pack valide avant de creer la fiche', 'warning');
                return;
              }
              const client = saveClient({
                categorie: 'nutrition',
                form: formData,
                prenom: formData.prenom,
                formule: 'nutrition',
                langue: 'FR',
                createdBy: 'anissa',
                status: 'questionnaire_envoye',
                packType,
                // V97.8.1 (2026-05-12) : packStartedAt = null à la création.
                // Avant ce fix, on posait Date.now() ici → la timeline du
                // pack 6 mois démarrait dès la création (Anissa voyait
                // 'Jour 1 du pack' avant même le RDV anamnèse / publication
                // du plan). Le pack démarre maintenant uniquement à
                // markDelivered (transitions.markDelivered).
                packStartedAt: null,
                packStartedAtConfirmed: false,
                packSchedule: [],
              });
              refreshClients();

              // V97.9 — Plus d'auto-activation app, plus d'ouverture Gmail.
              // Anissa pilote ces 2 actions depuis l'etape Onboarding du
              // parcours (BLOC 1 "Activer l'espace cliente" + BLOC 2
              // "Envoyer le pre-questionnaire"). Garde l'historique des
              // anciens flows V97.5/V97.6 dans le git log pour reference.
              showToast('Fiche créée — direction sa fiche');
              setTimeout(() => {
                setJustCreatedId(client.id);
                setClientId(client.id);
                setPage('clientJourney');
                setMobileMenu(false);
                try {
                  window.history.pushState({}, '', `/parcours/${client.id}`);
                } catch { /* navigation history non bloquante */ }
              }, 600);
            }}
            onCancel={goToDashboard}
          />
        )}

        {/* Edit own client OR shared Benoit client (anamnese update)
            V86.5 : on preserve les metadonnees existantes (categorie/formule/langue/
            createdBy/packType/...) pour eviter d'ecraser une cliente partagee Benoit
            (pack20/pack30/suivi/intensif + langue EN) quand Anissa modifie l'anamnese. */}
        {page === 'anissaEditClient' && clientId && (
          <AnissaClientForm
            initialForm={getClient(clientId)?.form}
            initialPackType={getClient(clientId)?.packType}
            clientId={clientId}
            onSave={async (formData, updatedPackType) => {
              const existing = getClient(clientId) || {};
              const existingPackKnown = getPackAccess(existing.packType).known === true;
              if (!existingPackKnown && getPackAccess(updatedPackType).known !== true) {
                showToast('Choisis un pack valide pour reparer cette ancienne fiche', 'warning');
                return;
              }
              if (!existingPackKnown) {
                if (!isCloudEnabled) {
                  showToast('Connexion Supabase requise pour reparer le pack', 'warning');
                  return;
                }
                const { data: repaired, error: repairError } = await supabase
                  .from('clients')
                  .update({ pack_type: updatedPackType })
                  .eq('id', clientId)
                  .select('pack_type')
                  .maybeSingle();
                if (repairError || repaired?.pack_type !== updatedPackType) {
                  showToast(repairError?.message || 'Le pack n a pas ete confirme par Supabase', 'warning');
                  return;
                }
              }
              try {
                await saveClientConfirmed({
                  // Spread existing first so metadata is preserved
                  ...existing,
                  // Then override only the fields Anissa can edit
                  id: clientId,
                  form: formData,
                  prenom: formData.prenom || existing.prenom,
                  // Ensure categorie stays set (fallback for legacy Anissa clients)
                  categorie: existing.categorie || 'nutrition',
                  // Keep existing formule/langue/createdBy/packType/packStartedAt/packSchedule
                  // untouched (fallback to nutrition/FR/anissa only if totally absent).
                  formule: existing.formule || 'nutrition',
                  langue: existing.langue || 'FR',
                  createdBy: existing.createdBy || 'anissa',
                  // Une fiche legacy sans pack reconnu peut etre reparee ici.
                  // Un pack deja connu reste immuable dans l'editeur d'anamnese.
                  packType: existingPackKnown ? existing.packType : updatedPackType,
                });
              } catch (error) {
                showToast(error?.message || 'La sauvegarde cloud de la fiche a échoué. Réessaie avant de quitter.', 'warning');
                throw error;
              }
              // 2026-07-21 (validation Anissa) : cocher « Sans chiffres »
              // coupe automatiquement le suivi de poids côté app cliente.
              // Anissa peut le réactiver à la main dans les réglages app de
              // la fiche si c'est pertinent. Fire-and-forget : sans compte
              // app (cliente pas en staging), il n'y a rien à couper.
              const sansChiffresVientDEtreCoche =
                formData.presentationSansChiffres === true &&
                existing.form?.presentationSansChiffres !== true;
              if (sansChiffresVientDEtreCoche) {
                updateClientAppConfig({ ...existing, form: formData }, { weight_tracking_enabled: false })
                  .then(() => showToast('Sans chiffres : suivi du poids désactivé dans son app', 'info'))
                  .catch((e) => {
                    // clinique-guard 2026-07-21 : seul le 404 (cliente sans
                    // compte app) est un silence acceptable. Tout autre échec
                    // doit prévenir Anissa — la transition est consommée, le
                    // couplage ne sera jamais retenté automatiquement.
                    if (e?.status === 404) return;
                    showToast('Sans chiffres coché, mais suivi du poids toujours actif dans son app — désactive-le à la main (réglages app de la fiche)', 'warning');
                  });
              }
              refreshClients();
              showToast('Client sauvegarde avec succes');
              returnToEditedClientJourney();
            }}
            onCancel={returnToEditedClientJourney}
          />
        )}

        {/* Phase H (2026-05-10) : NutritionConsultation supprimee du routing principal.
            Le composant existe toujours, utilise uniquement embedded dans l'etape 6
            du parcours cliente (cf. ClientJourneyPage.StepPlanEditing). */}

        {/* Nutrition History */}
        {page === 'nutritionHistory' && (
          <NutritionHistory
            clientId={clientId}
            onBack={goToDashboard}
            isAnissa={true}
            onEditConsultation={handleEditConsultation}
          />
        )}

        {/* 2026-08-25 — L'ecran « Chiffres » a ete RETIRE, composant supprime.
            Benoit s'occupe de toute la partie financiere et l'explique
            lui-meme a Anissa ; le recap mensuel vit desormais dans son outil
            de facturation.

            Il fallait le retirer de toute facon : il calculait le chiffre
            d'affaires depuis un `form.customRate` compte EN ENTIER pour
            chaque mois ecoule depuis la creation de la fiche, vestige d'un
            modele d'abonnement mensuel abandonne au profit des packs
            one-shot. Deux fiches portant 3000 (sans doute saisi par erreur,
            3000 etant l'objectif mensuel par defaut), il annoncait 6000 par
            mois depuis avril, soit ~30 000 de chiffre d'affaires imaginaire
            pour un seul pack reellement facture.

            La table `manual_revenues` est CONSERVEE : historique comptable
            d'une Sarl suisse, dix ans de retention. Elle n'est simplement
            plus lue par l'application. */}

        {/* V31 : Agenda partagé (côté Anissa) */}
        {page === 'sharedCalendar' && (
          <SharedCalendar
            currentUser="anissa"
            onBack={goToDashboard}
            onOpenClient={handleAnissaOpenClient}
          />
        )}

        {/* Phase D (2026-05-10) : Parcours cliente — wizard guide post-anamnese */}
        {page === 'clientJourney' && clientId && (
          <ClientJourneyPage
            clientId={clientId}
            justCreated={justCreatedId === clientId}
            onDismissJustCreated={() => setJustCreatedId(null)}
            onExit={handleExitClientJourney}
            onEditProfile={(options) => handleEditClientProfile(clientId, options)}
            onSendPackReview={handleSendPackReview}
            onViewHistory={handleViewNutritionHistory}
            resumeProgramUpdateScope={resumeProgramUpdateScope}
            onConsumeResumeProgramUpdateScope={() => setResumeProgramUpdateScope(null)}
          />
        )}

        {/* Bibliotheque Complements */}
        {page === 'anissaSupplements' && (
          <SupplementsLibrary />
        )}
      </div>
    );
  }

  // ─── Espace 100 % Anissa (2026-07-21) ───
  // L'interface Benoit a été retirée du SaaS : Benoit travaille dans son
  // propre cockpit (deploy-cockpit). currentUser vaut toujours 'Anissa'
  // (LoginScreen ne propose plus qu'elle), cette branche est inatteignable.
  return null;
}

export default App;
